import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { Checkbox } from "@/components/ui/checkbox";
import { FormSection, SelectField, TextAreaField, TextField } from "@/components/common/Forms";
import { FileUpload } from "@/components/common/FileUpload";
import { SuccessPanel } from "@/components/common/SuccessPanel";
import { COMPANY_TYPES, IMAGES, INDUSTRY_OPTIONS, REG_INTERESTS } from "@/data/site";

const INIT = { company_name: "", company_type: "", gst_number: "", industry: "", website: "", employee_count: "", sites_count: "", contact_name: "", designation: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", password: "", confirm: "" };

export default function VendorRegister() {
  const [f, setF] = useState(INIT);
  const [interests, setInterests] = useState([]);
  const [files, setFiles] = useState([]);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_type || !f.industry) return toast.error("Please select company type and industry.");
    if (f.password !== f.confirm) return toast.error("Passwords do not match.");
    if (!agree) return toast.error("Please confirm the details are accurate.");
    setBusy(true);
    try {
      const { confirm, ...payload } = f;
      await api.post("/auth/register", { ...payload, interests, attachment_ids: files.map((x) => x.id) });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vendor-register-page">
      <PageHero eyebrow="Vendor / customer registration" title="Register your business for portal access." text="Approved businesses get a dedicated portal for AMC, warranty, service and order requests with status tracking. Every registration is verified by our team." image={IMAGES.highrise} />
      <section className="container-x py-16">
        {done ? (
          <SuccessPanel title="Registration submitted for verification" text="Our team verifies company details (typically within 1–2 business days). You'll receive an email once your portal access is approved." actions={[{ to: "/", label: "Back to home" }, { to: "/login", label: "Go to login" }]} testid="register-success" />
        ) : (
          <form onSubmit={submit} className="card-base mx-auto max-w-4xl space-y-8 p-6 sm:p-10" data-testid="vendor-register-form">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> Use your official company email and GST / registration number. Duplicate or incomplete registrations are not approved.
            </div>
            <FormSection step="01" title="Company information">
              <TextField id="reg-company-name" label="Registered company name" value={f.company_name} onChange={set("company_name")} required />
              <SelectField id="reg-company-type" label="Company type" value={f.company_type} onChange={set("company_type")} options={COMPANY_TYPES} required />
              <TextField id="reg-gst" label="GSTIN / company registration no." value={f.gst_number} onChange={set("gst_number")} required hint="Used to prevent duplicate company accounts." />
              <SelectField id="reg-industry" label="Industry" value={f.industry} onChange={set("industry")} options={INDUSTRY_OPTIONS} required />
              <TextField id="reg-website" label="Website" value={f.website} onChange={set("website")} placeholder="https://" />
              <SelectField id="reg-employees" label="Employees" value={f.employee_count} onChange={set("employee_count")} options={["1–50", "51–200", "201–1,000", "1,000–5,000", "5,000+"]} />
              <SelectField id="reg-sites" label="Number of sites / facilities" value={f.sites_count} onChange={set("sites_count")} options={["1", "2–5", "6–20", "21–100", "100+"]} />
            </FormSection>
            <FormSection step="02" title="Primary contact">
              <TextField id="reg-contact-name" label="Full name" value={f.contact_name} onChange={set("contact_name")} required />
              <TextField id="reg-designation" label="Designation" value={f.designation} onChange={set("designation")} required placeholder="Facility Manager, Procurement Head…" />
              <TextField id="reg-email" type="email" label="Official email (login ID)" value={f.email} onChange={set("email")} required />
              <TextField id="reg-phone" label="Phone" value={f.phone} onChange={set("phone")} required />
            </FormSection>
            <FormSection step="03" title="Registered address">
              <TextAreaField id="reg-address" label="Address" rows={2} value={f.address} onChange={set("address")} required className="col-span-full" />
              <TextField id="reg-city" label="City" value={f.city} onChange={set("city")} required />
              <TextField id="reg-state" label="State" value={f.state} onChange={set("state")} required />
              <TextField id="reg-pincode" label="PIN code" value={f.pincode} onChange={set("pincode")} required />
            </FormSection>
            <FormSection step="04" title="Interests & verification documents">
              <div className="col-span-full grid gap-2 sm:grid-cols-3">
                {REG_INTERESTS.map((s) => (
                  <label key={s} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2.5 text-sm hover:border-slate-400">
                    <Checkbox checked={interests.includes(s)} onCheckedChange={() => setInterests((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))} data-testid={`reg-interest-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} /> {s}
                  </label>
                ))}
              </div>
              <div className="col-span-full"><FileUpload id="reg-files" files={files} onChange={setFiles} max={3} label="GST certificate / incorporation proof (optional, speeds up approval)" /></div>
            </FormSection>
            <FormSection step="05" title="Portal credentials">
              <TextField id="reg-password" type="password" label="Password (min 8 characters)" value={f.password} onChange={set("password")} required minLength={8} />
              <TextField id="reg-confirm" type="password" label="Confirm password" value={f.confirm} onChange={set("confirm")} required minLength={8} />
            </FormSection>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="reg-agree-checkbox" className="mt-0.5" />
              I confirm these details are accurate and I am authorised to register on behalf of this company.
            </label>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <button type="submit" disabled={busy} className="btn-primary" data-testid="vendor-register-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit registration</button>
              <p className="text-sm text-slate-600">Already approved? <Link to="/login" className="font-semibold text-[#C20500]" data-testid="register-login-link">Sign in</Link></p>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
